<template>
  <b-modal
      id="modal-signup"
      size="size-signup"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
      centered
      modal-class="modal-mobile-fullscreen modal-sign-up"
  >
    <PoweredBy v-if="showPoweredBy"/>
    <div class="d-block text-center mt-1 mt-lg-3 mb-3 logo preview-logo">
      <img :src="getAppLogo" class="img-fluid" alt="Logo">
    </div>

    <div class="d-block text-center">
      <h5 v-if="getAppConfigReady && loginSubtitle" v-html="loginSubtitle" class="mb-3 intro-pre-title"></h5>
      <h1 class="modal-title pb-2">
        {{ eventName ? eventName : $t('pages.preview.signup_modal_title') }}
      </h1>

      <div v-if="emailMismatch" class="account-step__info mt-2 mb-3">
        <div class="in">
          <div class="text-left small">
            {{ $t('auth.email_mismatch_warning', {email: prefilledEmail}) }}
          </div>
        </div>
      </div>

      <button
          v-if="pendingAuth"
          class="btn btn-primary btn-block mt-3"
          @click="handleSwitchAccount"
          :disabled="switchingAccount"
      >
        <span v-if="switchingAccount">{{ $t('common.loading') }}</span>
        <span v-else>{{ $t('auth.switch_account') }}</span>
      </button>
    </div>

    <div class="signup-modal-content" :class="socialPositionClass" v-if="!pendingAuth">
      <!-- Main Forms -->
      <div class="main-form">
        <!-- Primary Provider - only ONE shown at a time -->
        <MagicLinkForm
            v-if="currentMode === 'magic_link'"
            @step-change="handleStepChange"
        />

        <EmailTokenUnifiedForm
            v-else-if="currentMode === 'email_token'"
            @success="handleSignupSuccess"
            @step-change="handleStepChange"
        />

        <PasswordSignupForm
            v-else-if="currentMode === 'password'"
            @success="handleSignupSuccess"
        />
      </div>
      <!-- Social Login Buttons -->
      <div class="social-buttons">
        <SocialAuthButtons
            :show="showSocialButtons"
            :title-key="socialPosition === 'top' ? 'forms.login_form.social_login_title_top' : undefined"
            :apple-disabled="appleDisabled"
            @success="handleSignupSuccess"
            @error="handleSocialError"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'

// Provider components
import EmailTokenUnifiedForm from '@/domain/auth/components/providers/EmailTokenUnifiedForm.vue'
import MagicLinkForm from '@/domain/auth/components/providers/MagicLinkForm.vue'
import PasswordSignupForm from '@/domain/auth/components/providers/PasswordSignupForm.vue'
import SocialAuthButtons from '@/domain/auth/components/SocialAuthButtons.vue'
import PoweredBy from "@/components/PoweredBy.vue";

export default {
  name: 'ModalSignup',

  props: {
    eventName: {
      type: String,
      default: null
    },
    emailMismatch: {
      type: Boolean,
      default: false
    },
    pendingAuth: {
      type: Object,
      default: null
    }
  },

  components: {
    PoweredBy,
    EmailTokenUnifiedForm,
    MagicLinkForm,
    PasswordSignupForm,
    SocialAuthButtons
  },

  data() {
    return {
      socialError: false,
      currentMode: null, // 'email_token' or 'password'
      emailTokenStep: 'email-input', // Track current step of email token form
      switchingAccount: false
    }
  },

  computed: {
    ...mapGetters(['thirdLevelDomain', 'disableSocialLoginInPreview', 'showMarketingCheck', 'isPicaThirdLevelDomain']),
    ...mapGetters('appConfig', ['getAppConfigReady', 'loginSubtitle', 'getAppLogo', 'getHidePoweredByLogo']),
    ...mapGetters('auth', [
      'enabledAuthProviders',
      'isAuthProviderEnabled',
      'hasSocialLoginEnabled',
      'primaryAuthProvider',
      'prefilledEmail'
    ]),


    hasSocialProviders() {
      return this.isProviderEnabled('google') ||
          this.isProviderEnabled('facebook') ||
          (this.isProviderEnabled('apple') && !this.appleDisabled)
    },

    showSocialButtons() {
      // Show social buttons only on initial email step for email_token/magic_link
      // Always show for password mode
      if (this.currentMode === 'password') {
        return true
      }

      if (this.currentMode === 'email_token' || this.currentMode === 'magic_link') {
        return this.emailTokenStep === 'email-input'
      }

      return true
    },

    socialPosition() {
      if (['photos'].includes(this.thirdLevelDomain)) return 'top'
      return 'bottom'
    },

    socialPositionClass() {
      return `social-position-${this.socialPosition}`
    },

    appleDisabled() {
      return this.disableSocialLoginInPreview
    },
    showPoweredBy() {
      return this.getAppConfigReady && !this.isPicaThirdLevelDomain && !this.getHidePoweredByLogo
    },
  },

  mounted() {
    // If emailMismatch, logout current user to allow login with correct account
    if (this.emailMismatch) {
      this.logout()
    }

    // If email is prefilled (from preview), use magic_link or email_token based on what's enabled
    if (this.prefilledEmail) {
      if (this.isProviderEnabled('magic_link')) {
        this.currentMode = 'magic_link'
      } else {
        this.currentMode = 'email_token'
      }
    } else {
      // Use primary provider (magic_link > email_token > password)
      this.currentMode = this.primaryAuthProvider
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'authenticate']),

    isProviderEnabled(provider) {
      return this.isAuthProviderEnabled(provider)
    },

    handleStepChange(step) {
      // Track email token form step to hide/show social buttons
      this.emailTokenStep = step
    },

    handleSignupSuccess() {
      // Close modal
      this.$bvModal.hide('modal-signup')

      // Redirect to checkins list
      this.$router.push({name: 'checkins-list'})

      // Show marketing consent modal if needed (after navigation starts)
      this.$nextTick(() => {
        if (this.showMarketingCheck) {
          this.$bvModal.show('modal-marketing-consent')
        }
      })
    },

    handleSocialError(error) {
      console.error('Social signup error:', error)
      this.socialError = true
      setTimeout(() => {
        this.socialError = false
      }, 5000)
    },

    async handleSwitchAccount() {
      if (!this.pendingAuth) return

      this.switchingAccount = true
      try {
        await this.logout()
        await this.authenticate({
          method: this.pendingAuth.type,
          credentials: this.pendingAuth,
          remember: true
        })
        this.handleSignupSuccess()
      } catch (error) {
        this.switchingAccount = false
      }
    }
  }
}
</script>